import { ActionIcon, Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconExternalLink, IconFolderPlus, IconUpload } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { AddExternalLinkModal } from 'src/Components/Modals/AddExternalLinkModal';
import { CreateDirectoryModal } from 'src/Components/Modals/CreateDirectoryModal';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { FileUploadButton } from 'src/Components/Shared/FileUploadButton';
import { VideoTutorialButton } from 'src/Components/Shared/VideoTutorialButton';
import { useAddExternalLink } from 'src/Hooks/Mutations/useAddExternalLink';
import { useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import { useCanCreate, useCanRead } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useDocumentPathStore from 'src/Stores/documentPathStore';

import DocumentBrowser from '../../../Components/Documents/DocumentBrowser/DocumentBrowser.component';

import styles from './DocumentsPage.module.scss';

const DocumentsPage = () => {
  const { t } = useTranslation();

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('assetStorage');
  const { hasPermission: canCreate } = useCanCreate('assetStorage');

  const location = useLocation();
  const openUploadFromState = (location.state as { openUpload?: boolean })?.openUpload ?? false;
  const [triggerOpen, setTriggerOpen] = useState(openUploadFromState);

  useEffect(() => {
    if (openUploadFromState) {
      setTriggerOpen(true);
      // Reset after triggering to prevent re-opening on re-render
      const timer = setTimeout(() => setTriggerOpen(false), 100);
      return () => clearTimeout(timer);
    }
  }, [openUploadFromState]);

  const [
    createDirectoryModalOpened,
    { open: openCreateDirectoryModal, close: closeCreateDirectoryModal },
  ] = useDisclosure();
  const [externalLinkModalOpen, { open: openExternalLinkModal, close: closeExternalLinkModal }] =
    useDisclosure();
  const { currentPath } = useDocumentPathStore();

  const { startUpload, isLoading } = useFileUploadManager();

  const { mutate: addExternalLink, isLoading: isAddingLink } = useAddExternalLink({
    onSuccess: closeExternalLinkModal,
  });

  const isMobile = useIsMobile();

  const assetAboveQuotas = useIsAssetAboveQuotas();

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('documentsPage.title', 'Documents')}
        rightElement={
          <div className={styles.buttons}>
            <VideoTutorialButton videoUrl={'https://youtu.be/EtMua1vcd8Q'} />
            {canCreate && (
              <>
                {isMobile ? (
                  <ActionIcon
                    radius={'xl'}
                    size={'lg'}
                    onClick={openCreateDirectoryModal}
                    variant={'outline'}
                  >
                    <IconFolderPlus size={20} />
                  </ActionIcon>
                ) : (
                  <Button
                    variant={'outline'}
                    leftSection={<IconFolderPlus size={20} />}
                    onClick={openCreateDirectoryModal}
                    disabled={assetAboveQuotas}
                  >
                    {t('documentsPage.addDirectory', 'Ajouter un dossier')}
                  </Button>
                )}
                {isMobile ? (
                  <ActionIcon
                    radius={'xl'}
                    size={'lg'}
                    onClick={openExternalLinkModal}
                    variant={'outline'}
                    disabled={assetAboveQuotas}
                  >
                    <IconExternalLink size={20} />
                  </ActionIcon>
                ) : (
                  <Button
                    variant={'outline'}
                    leftSection={<IconExternalLink size={20} />}
                    onClick={openExternalLinkModal}
                    disabled={assetAboveQuotas}
                  >
                    {t('documents.externalLink.button', 'Ajouter un lien URL')}
                  </Button>
                )}
                <FileUploadButton
                  leftSection={<IconUpload size={20} />}
                  label={t('documentsPage.addDocument', 'Ajouter un document')}
                  disabled={assetAboveQuotas}
                  onSelect={files => startUpload(files, currentPath)}
                  loading={isLoading}
                  compact={isMobile}
                  multiple
                  triggerOpen={triggerOpen}
                />
              </>
            )}
          </div>
        }
      />
      <DocumentBrowser />
      <CreateDirectoryModal
        open={createDirectoryModalOpened}
        path={currentPath}
        onClose={closeCreateDirectoryModal}
      />
      <AddExternalLinkModal
        open={externalLinkModalOpen}
        onClose={closeExternalLinkModal}
        loading={isAddingLink}
        onValidate={({ name, url }) => addExternalLink({ name, url, path: currentPath })}
      />
    </div>
  );
};

export default DocumentsPage;
